import { useTranslation } from 'next-i18next'

import { useCallback } from 'react'

import { PRODUCT_CATEGORY } from '~/types/product'
import todAnalytics from '~/analytics/tod/todAnalytics'
import { Product } from '~/analytics/types/analytics.type'
import { Order } from '~/types/order'

interface Param {
  logPurchase: (revenue: number, order: Order) => void
  logCheckout: (order: Order) => void
  logTodFailure: (event: string, order?: Record<string, any>) => void
}

export default function useLogEcommerce(): Param {
  const { t } = useTranslation('booking')

  const prepareProductsForLog = useCallback(
    (ord: Order): Product[] => {
      return ord.products.map((product) => {
        const label = t(`tutorLevel.${product.mentorLevel}`, { lng: 'en' })

        return {
          item_id: product.productId,
          item_name: `${label} ${product.travelExpense ? 'incl. Travel' : ''}`.trim(),
          item_category: PRODUCT_CATEGORY.PRIVATE_TUTORING,
          price: Math.round((product.priceTotal / product.hours + Number.EPSILON) * 100) / 100,
          quantity: product.hours,
        }
      })
    },
    [t],
  )

  const logPurchase = useCallback(
    (revenue: number, order: Order) => {
      if (!order) {
        throw new Error('Order cannot be null.')
      }

      const products = prepareProductsForLog(order)

      todAnalytics.logPurchase({
        orderId: order.orderId,
        currencyCode: order.currency,
        revenue,
        products,
      })
    },
    [prepareProductsForLog],
  )

  const logCheckout = useCallback(
    (order: Order) => {
      const products = prepareProductsForLog(order)
      todAnalytics.logCheckout({ products, currencyCode: order.currency, revenue: order.priceTotal })
    },
    [prepareProductsForLog],
  )

  const logTodFailure = useCallback((event: string, order?: Record<string, any>) => {
    todAnalytics.logTodFailure(event, { ...order })
  }, [])

  return { logPurchase, logCheckout, logTodFailure }
}
